import React from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { SvgIconComponent } from '@mui/icons-material'
import {
  ReceiptOutlined,
  Inventory2Outlined,
  LocalOfferOutlined,
  PriceChangeOutlined,
  ComputerOutlined,
  PeopleOutlined,
  AccountBalanceOutlined,
  AltRoute as AltRouteIcon,
  Difference as DifferenceIcon,
} from '@mui/icons-material'
import type { ExceptionDataDomain } from '../types/exceptions'
import type { Market } from '../constants/markets'
import { getMarketForCountry } from '../constants/markets'

// ─── External system icons (Lightspeed / Xero / Skimmer / Pooltrackr / Google Drive) ───

/**
 * Lightspeed system icon with favicon and SVG fallback
 */
export const LightspeedIcon: React.FC<{ sx?: any; size?: number }> = ({ 
  sx, 
  size = 24 
}) => (
  <>
    <img
      src="/icons/lightspeed.svg"
      alt="Lightspeed"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '4px',
        ...sx
      }}
      onError={(e) => {
        // Fallback to SVG icon if favicon fails
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#0073e6"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">LS</text>
    </svg>
  </>
)

/**
 * Xero system icon with favicon and SVG fallback
 */
export const XeroIcon: React.FC<{ sx?: any; size?: number }> = ({ 
  sx, 
  size = 24 
}) => (
  <>
    <img
      src="/icons/xero.svg"
      alt="Xero"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '4px',
        ...sx
      }}
      onError={(e) => {
        // Fallback to SVG icon if favicon fails
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#13b5ea"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">X</text>
    </svg>
  </>
)

/**
 * Skimmer system icon (for completeness)
 */
export const SkimmerIcon: React.FC<{ sx?: any; size?: number }> = ({ 
  sx, 
  size = 24 
}) => (
  <>
    <img
      src="/icons/skimmer.ico"
      alt="Skimmer"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '4px',
        ...sx
      }}
      onError={(e) => {
        // Fallback to SVG icon if favicon fails
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#1976d2"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">SK</text>
    </svg>
  </>
)

/**
 * Freshdesk system icon. Loads the official brand logo from
 * `/icons/freshdesk.svg` (drop the asset there - same convention as
 * `lightspeed.svg` / `xero.svg`); until that file exists it falls back to an
 * inline green speech-bubble placeholder so the icon is never blank.
 *
 * Decorative (`alt=""` / `aria-hidden`): every placement pairs it with adjacent
 * text (toggle label, link URL, card title, page header), so it must not
 * double-announce. Sizes from `size`, or - so it drops cleanly into the MUI
 * `SvgIconComponent` slots that size via `sx={{ fontSize }}` (admin landing
 * cards, the AdminPageHeader tile) - from a numeric `sx.fontSize`.
 */
export const FreshdeskIcon: React.FC<{ sx?: SxProps<Theme>; size?: number }> = ({
  sx,
  size,
}) => {
  const fontSize = (sx as { fontSize?: unknown } | undefined)?.fontSize
  const px = size ?? (typeof fontSize === 'number' ? fontSize : 24)
  return (
    <>
      <Box
        component="img"
        src="/icons/freshdesk.svg"
        alt=""
        aria-hidden
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const target = e.currentTarget
          target.style.display = 'none'
          target.nextElementSibling?.setAttribute('style', 'display:inline-block')
        }}
        sx={{
          display: 'block',
          width: px,
          height: px,
          borderRadius: '4px',
          flexShrink: 0,
          ...sx,
        }}
      />
      <Box
        component="svg"
        viewBox="0 0 24 24"
        width={px}
        height={px}
        aria-hidden
        sx={{ display: 'none', flexShrink: 0 }}
      >
        <rect width="24" height="24" rx="4" fill="#25C16F" />
        <path
          d="M6 7.6c0-.9.7-1.6 1.6-1.6h8.8c.9 0 1.6.7 1.6 1.6v5.6c0 .9-.7 1.6-1.6 1.6H10l-3.1 2.3c-.4.3-.9 0-.9-.5V7.6Z"
          fill="#ffffff"
        />
      </Box>
    </>
  )
}

/**
 * Confluence system icon. Same shape as `FreshdeskIcon`: an `<img>` of
 * `/icons/confluence.svg` with an inline Atlassian-blue SVG fallback (so it
 * renders even before a brand asset is dropped in). Decorative (`alt=""` /
 * `aria-hidden`) - always paired with adjacent text. Sizes from `size`, or from a
 * numeric `sx.fontSize` so it drops into the MUI `SvgIconComponent` slots.
 */
export const ConfluenceIcon: React.FC<{ sx?: SxProps<Theme>; size?: number }> = ({
  sx,
  size,
}) => {
  const fontSize = (sx as { fontSize?: unknown } | undefined)?.fontSize
  const px = size ?? (typeof fontSize === 'number' ? fontSize : 24)
  return (
    <>
      <Box
        component="img"
        src="/icons/confluence.svg"
        alt=""
        aria-hidden
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const target = e.currentTarget
          target.style.display = 'none'
          target.nextElementSibling?.setAttribute('style', 'display:inline-block')
        }}
        sx={{
          display: 'block',
          width: px,
          height: px,
          borderRadius: '4px',
          flexShrink: 0,
          ...sx,
        }}
      />
      <Box
        component="svg"
        viewBox="0 0 24 24"
        width={px}
        height={px}
        aria-hidden
        sx={{ display: 'none', flexShrink: 0 }}
      >
        <rect width="24" height="24" rx="4" fill="#0052CC" />
        <path
          d="M6.2 15.3c-.3.5-.1 1.1.4 1.3l3.1 1.4c.5.2 1.1 0 1.3-.5.6-1.2 1.4-2 2.5-1.8 1 .2 1.7.9 2.6 2.6.2.5.8.7 1.3.5l3-1.4c.5-.2.7-.8.4-1.3-.6-1.1-2.4-4-3.6-4.7-1.6-1-3.2-.6-4.9 1.2-1 1.1-1.9 2-2.6 2.8Z"
          fill="#ffffff"
          opacity="0.95"
        />
        <path
          d="M17.8 8.7c.3-.5.1-1.1-.4-1.3l-3.1-1.4c-.5-.2-1.1 0-1.3.5-.6 1.2-1.4 2-2.5 1.8-1-.2-1.7-.9-2.6-2.6-.2-.5-.8-.7-1.3-.5l-3 1.4c-.5.2-.7.8-.4 1.3.6 1.1 2.4 4 3.6 4.7 1.6 1 3.2.6 4.9-1.2 1-1.1 1.9-2 2.6-2.8Z"
          fill="#ffffff"
        />
      </Box>
    </>
  )
}

/**
 * Pooltrackr system icon
 */
export const PooltrackrIcon: React.FC<{ sx?: any; size?: number }> = ({
  sx, 
  size = 24 
}) => (
  <>
    <img
      src="/icons/pooltrackr.png"
      alt="Pooltrackr"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '4px',
        ...sx
      }}
      onError={(e) => {
        // Fallback to SVG icon if favicon fails
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#e84142"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">PT</text>
    </svg>
  </>
)

/**
 * Google Drive icon
 */
export const GoogleDriveIcon: React.FC<{ sx?: any; size?: number }> = ({ 
  sx, 
  size = 24 
}) => (
  <>
    <img
      src="/icons/google-drive.svg"
      alt="Google Drive"
      width={size}
      height={size}
      style={{
        display: 'block',
        borderRadius: '4px',
        ...sx
      }}
      onError={(e) => {
        // Fallback to SVG icon if image fails
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#4285f4"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">GD</text>
    </svg>
  </>
)

/**
 * Google "G" brand mark - 2026 multicolour artwork from `/icons/google-g.svg`.
 * Used on auth surfaces ("Sign in with Google" / "Connect with Google Account").
 * The asset is a fixed multicolour SVG, so do NOT pass a ``color`` override.
 */
export const GoogleIcon: React.FC<{ sx?: any; size?: number }> = ({
  sx,
  size = 20,
}) => (
  <>
    <img
      src="/icons/google-g.svg"
      alt="Google"
      width={size}
      height={size}
      style={{
        display: 'block',
        ...sx
      }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        target.nextElementSibling?.setAttribute('style', 'display: inline-block');
      }}
    />
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none', ...sx }}
    >
      <rect width="24" height="24" rx="4" fill="#4285f4"/>
      <text x="12" y="16" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">G</text>
    </svg>
  </>
)

/**
 * Google Sheets icon - 2026 gradient artwork from `/icons/google-sheets.svg`.
 * Used to mark COGS rows / chips sourced from the Google Sheets register.
 *
 * Single ``<img>`` (not the Fragment+fallback pattern used by Lightspeed/Xero/Drive)
 * so MUI's ``<Chip icon={...}>`` - which clones the element and injects
 * ``MuiChip-icon`` - has a real DOM target for its spacing styles and any
 * ``sx`` override hitting that class.
 */
export const GoogleSheetsIcon: React.FC<{ sx?: SxProps<Theme>; size?: number; className?: string }> = ({
  sx,
  size = 16,
  className,
}) => (
  <Box
    component="img"
    src="/icons/google-sheets.svg"
    alt="Google Sheets"
    width={size}
    height={size}
    className={className}
    sx={{ display: 'inline-block', flexShrink: 0, ...sx }}
  />
)

/**
 * Microsoft Excel icon - inline SVG (no external asset).
 * Rendered in muted grey rather than Excel green to read as "legacy /
 * superseded" data alongside the brighter GoogleSheetsIcon.
 *
 * Forwards ``className`` so MUI's ``<Chip icon={...}>`` can attach
 * MuiChip-icon styles (see GoogleSheetsIcon for the same rationale).
 */
export const ExcelIcon: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    role="img"
    aria-label="Microsoft Excel (legacy)"
    className={className}
    style={{ display: 'inline-block', flexShrink: 0 }}
  >
    <rect x="2" y="2" width="20" height="20" rx="3" fill="#757575" />
    <text
      x="12"
      y="17"
      textAnchor="middle"
      fill="white"
      fontSize="14"
      fontWeight={700}
      fontFamily="sans-serif"
    >
      X
    </text>
  </svg>
)

/**
 * Inspector tool icon - the 🕵️ spy emoji as a PNG image (Microsoft Fluent
 * Emoji 3D "Detective", the Windows-style render).
 *
 * Rendered from a bundled asset rather than a font glyph so it looks identical
 * on every OS/browser instead of varying with the platform emoji font. One
 * source for every "Inspector" surface (headers + action buttons).
 */
export const InspectorIcon: React.FC<{ size?: number; sx?: SxProps<Theme> }> = ({
  size = 24,
  sx,
}) => (
  <Box
    component="img"
    src="/icons/inspector.png"
    alt="Inspector"
    width={size}
    height={size}
    sx={{ display: 'inline-block', flexShrink: 0, ...sx }}
  />
)

/**
 * Get system icon component by system name
 */
export const getSystemIcon = (system: string, props?: { sx?: any; size?: number }) => {
  switch (system.toLowerCase()) {
    case 'lightspeed':
      return <LightspeedIcon {...props} />
    case 'xero':
      return <XeroIcon {...props} />
    case 'skimmer':
      return <SkimmerIcon {...props} />
    case 'freshdesk':
      return <FreshdeskIcon {...props} />
    case 'confluence':
      return <ConfluenceIcon {...props} />
    case 'pooltrackr':
      return <PooltrackrIcon {...props} />
    case 'googledrive':
    case 'google-drive':
      return <GoogleDriveIcon {...props} />
    case 'googlesheets':
    case 'google-sheets':
      return <GoogleSheetsIcon size={props?.size} />
    case 'google':
      return <GoogleIcon {...props} />
    default:
      return null
  }
}

// ─── Exception domain icons (sales / stock / product setup / pricing / system) ───

export type ExceptionDomainVariant = 'filled' | 'outlined'

export interface ExceptionDomainMeta {
  key: ExceptionDataDomain
  label: string
  description: string
  color: string
  /** Colour emoji used for dashboard / heading contexts (variant="filled"). */
  emoji: string
  /** Monochrome MUI outline icon used for menus / headings (variant="outlined"). */
  outlined: SvgIconComponent
  /** Markets in which this domain has any visible exception types. Undefined = all. */
  markets?: ReadonlyArray<Market>
}

/**
 * Single source of truth for exception-domain visuals.
 * Keyed by the canonical backend domain name (`sale` — singular, as returned
 * by the API). Consumers that receive `sales` from older payloads should go
 * through `getDomainMeta()` which normalises the alias.
 */
const POOLWERX_TEAL = '#007f98'

export const EXCEPTION_DOMAIN_META: Record<ExceptionDataDomain, ExceptionDomainMeta> = {
  sale: {
    key: 'sale',
    label: 'Sales',
    description: 'Sales transactions and invoicing exceptions',
    color: POOLWERX_TEAL,
    emoji: '🧾',
    outlined: ReceiptOutlined,
  },
  stock_management: {
    key: 'stock_management',
    label: 'Stock Management',
    description: 'On-hand stock, transfers, and inventory movement exceptions',
    color: POOLWERX_TEAL,
    emoji: '📦',
    outlined: Inventory2Outlined,
  },
  product_setup: {
    key: 'product_setup',
    label: 'Product Setup',
    description: 'Product catalogue, categorisation, and metadata exceptions',
    color: POOLWERX_TEAL,
    emoji: '🏷️',
    outlined: LocalOfferOutlined,
  },
  product_pricing: {
    key: 'product_pricing',
    label: 'Product Pricing',
    description: 'Pricing accuracy, margin, and promotion exceptions',
    color: POOLWERX_TEAL,
    emoji: '💵',
    outlined: PriceChangeOutlined,
  },
  system: {
    key: 'system',
    label: 'System',
    description: 'System configuration and connectivity exceptions',
    color: POOLWERX_TEAL,
    emoji: '🖥️',
    outlined: ComputerOutlined,
  },
  client: {
    key: 'client',
    label: 'Client',
    description: 'Client record and linkage exceptions',
    color: POOLWERX_TEAL,
    emoji: '👥',
    outlined: PeopleOutlined,
    // Skimmer-Sync customer-mapping failures are the only client-domain
    // exception type and they only run for the US market. ANZ FPs see no
    // client exceptions, so the dropdown / overview cards hide the domain.
    markets: ['USA'],
  },
  accounting: {
    key: 'accounting',
    label: 'Accounting',
    description: 'Chart of accounts, GL postings, and period reconciliation exceptions',
    color: POOLWERX_TEAL,
    emoji: '📒',
    outlined: AccountBalanceOutlined,
  },
}

const DOMAIN_ALIASES: Record<string, ExceptionDataDomain> = {
  sales: 'sale',
}

/**
 * Normalise a (possibly aliased) domain key and return its meta.
 * Returns a neutral fallback for unknown domains so UI doesn't crash.
 */
export const getDomainMeta = (domain: string): ExceptionDomainMeta => {
  const normalised = (DOMAIN_ALIASES[domain] ?? domain) as ExceptionDataDomain
  return (
    EXCEPTION_DOMAIN_META[normalised] ?? {
      key: normalised,
      label: domain.charAt(0).toUpperCase() + domain.slice(1),
      description: `${domain} related exceptions`,
      color: '#9e9e9e',
      emoji: '📋',
      outlined: ReceiptOutlined,
    }
  )
}

// ─── Exception classification: Exception Type + Business Area ───

export interface ClassificationMeta {
  value: string
  label: string
  /** Solid chip background; chip text is white. */
  color: string
  /** Optional leading icon (used by the subtle Exception Type pill). */
  icon?: SvgIconComponent
}

/** Business classification of a rule (process vs data). */
export const EXCEPTION_TYPE_META: Record<string, ClassificationMeta> = {
  process_issue: {
    value: 'process_issue',
    label: 'Process Issue',
    color: '#6a1b9a',
    icon: AltRouteIcon,
  },
  data_discrepancy: {
    value: 'data_discrepancy',
    label: 'Data Discrepancy',
    color: '#00695c',
    icon: DifferenceIcon,
  },
}

/** Owning business area of a rule. */
export const BUSINESS_AREA_META: Record<string, ClassificationMeta> = {
  operations: { value: 'operations', label: 'Operations', color: POOLWERX_TEAL },
  it: { value: 'it', label: 'IT', color: '#5c6bc0' },
  marketing: { value: 'marketing', label: 'Marketing', color: '#EC1C5A' },
  finance: { value: 'finance', label: 'Finance', color: '#2e7d32' },
  support: { value: 'support', label: 'Support', color: '#f57c00' },
  other: { value: 'other', label: 'Other', color: '#757575' },
}

const titleCase = (value: string): string =>
  value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

/** Resolve Exception Type meta, with a neutral fallback for unknown values. */
export const getExceptionTypeMeta = (value: string): ClassificationMeta =>
  EXCEPTION_TYPE_META[value] ?? { value, label: titleCase(value), color: '#9e9e9e' }

/** Resolve Business Area meta, with a neutral fallback for unknown values. */
export const getBusinessAreaMeta = (value: string): ClassificationMeta =>
  BUSINESS_AREA_META[value] ?? { value, label: titleCase(value), color: '#9e9e9e' }

export interface ExceptionDomainIconProps {
  domain: string
  variant?: ExceptionDomainVariant
  size?: number
  sx?: SxProps<Theme>
  /** Apply the registry colour to the outlined icon (ignored for `filled` emoji). Defaults to false. */
  applyColor?: boolean
}

export const ExceptionDomainIcon: React.FC<ExceptionDomainIconProps> = ({
  domain,
  variant = 'outlined',
  size = 24,
  sx,
  applyColor = false,
}) => {
  const meta = getDomainMeta(domain)
  if (variant === 'filled') {
    return (
      <Box
        component="span"
        sx={{
          fontSize: size,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
      >
        {meta.emoji}
      </Box>
    )
  }
  const IconComponent = meta.outlined
  return (
    <IconComponent
      htmlColor={applyColor ? meta.color : undefined}
      sx={{
        fontSize: size,
        ...sx,
      }}
    />
  )
}

/**
 * Iteration order for exception-domain UI surfaces (dashboards, dropdowns).
 * Exported so consumers render a consistent sequence.
 */
export const EXCEPTION_DOMAIN_ORDER: ExceptionDataDomain[] = [
  'sale',
  'product_pricing',
  'product_setup',
  'stock_management',
  'accounting',
  'client',
  'system',
]

export interface ExceptionDomainDropdownItem {
  label: string
  path: string
  icon: React.ReactElement
  /** Domain key, propagated so consumers can re-apply gating later. */
  domain: ExceptionDataDomain
  /** Markets this domain is visible in (undefined = all). */
  markets?: ReadonlyArray<Market>
  /** Optional FP_USER access reference - when set, the standard
   * `userCanAccessInspectorItem` filter hides the item for FP_USER if
   * the page id is not in `FP_USER_ALLOWED_PAGES`. */
  fpUserAccess?: { kind: 'page'; id: string }
}

/**
 * Per-domain FP_USER access ids. Empty means "no FP_USER gate" - the
 * domain shows for staff and FP_USER alike (subject to other filters).
 * To hide a domain from FP_USER, register its page id here AND leave it
 * out of FP_USER_ALLOWED_PAGES; to expose it, also add the id to
 * FP_USER_ALLOWED_PAGES (and the backend mirror).
 */
const DOMAIN_FP_USER_PAGE_ID: Partial<Record<ExceptionDataDomain, string>> = {
  client: 'client_exceptions',
}

/**
 * True when a domain's `markets` allowlist (if any) admits the given
 * country code. Country codes are normalised via `getMarketForCountry`.
 * Domains without a `markets` allowlist are always visible. Market-gated
 * domains fail closed while country is loading - otherwise the nav
 * dropdown flashes US-only items (e.g. `client`) for ANZ FPs in the
 * window before `/franchise-partners/{fp}` resolves.
 */
export const isDomainVisibleInMarket = (
  meta: ExceptionDomainMeta,
  country: string | undefined,
): boolean => {
  if (!meta.markets) return true
  if (!country) return false
  const market = getMarketForCountry(country)
  return market !== null && meta.markets.includes(market)
}

/**
 * True when a domain is visible to the given role. Currently only the
 * `client` domain has a role gate (its only exception type is staff-only),
 * so for now this just hides `client` from FP_USER. Extend if more
 * domains need role-based hiding.
 */
export const isDomainVisibleToRole = (
  domain: ExceptionDataDomain,
  role: string | undefined,
): boolean => {
  if (role !== 'FP_USER') return true
  return DOMAIN_FP_USER_PAGE_ID[domain] === undefined
}

/**
 * Produce navigation-dropdown items (one per domain) from the registry,
 * using the canonical url slug and label. Domains whose `markets` allowlist
 * excludes the current FP's country are filtered out so the dropdown only
 * shows tabs the user has any chance of finding rows under.
 *
 * Matches the paths used in `inspectorNavigation.ts` today
 * (`exceptions/sales`, `exceptions/stock-management`, `exceptions/product-setup`,
 * `exceptions/product-pricing`, `exceptions/systems`, `exceptions/client`,
 * `exceptions/accounting`).
 */
export const exceptionDomainDropdownItems = (
  variant: ExceptionDomainVariant = 'outlined',
  ctx?: { country?: string },
): ExceptionDomainDropdownItem[] =>
  EXCEPTION_DOMAIN_ORDER
    .map((key) => EXCEPTION_DOMAIN_META[key])
    .filter((meta) => isDomainVisibleInMarket(meta, ctx?.country))
    .map((meta) => {
      const pageId = DOMAIN_FP_USER_PAGE_ID[meta.key]
      return {
        label: meta.label,
        path: `exceptions/${domainToSlug(meta.key)}`,
        icon: <ExceptionDomainIcon domain={meta.key} variant={variant} />,
        domain: meta.key,
        markets: meta.markets,
        ...(pageId ? { fpUserAccess: { kind: 'page' as const, id: pageId } } : {}),
      }
    })

/**
 * Canonical backend-key → URL-slug mapping for exception domains.
 * Shared across the inspector so links like `/exceptions/stock-management`
 * stay consistent. Aliases (e.g. `sales`) are normalised via `getDomainMeta`.
 */
export const domainToSlug = (domain: ExceptionDataDomain): string => {
  switch (domain) {
    case 'sale':
      return 'sales'
    case 'stock_management':
      return 'stock-management'
    case 'product_setup':
      return 'product-setup'
    case 'product_pricing':
      return 'product-pricing'
    case 'client':
      return 'client'
    case 'system':
      return 'systems'
    case 'accounting':
      return 'accounting'
  }
}

/**
 * Reverse of `domainToSlug` — maps the URL slug used in exception routes back
 * to the canonical backend domain key. Used by domain list pages so they can
 * pull heading/icon metadata from `EXCEPTION_DOMAIN_META` via the URL param.
 */
export const SLUG_TO_DOMAIN: Record<string, ExceptionDataDomain> = {
  'sales': 'sale',
  'stock-management': 'stock_management',
  'product-setup': 'product_setup',
  'product-pricing': 'product_pricing',
  'systems': 'system',
  'client': 'client',
  'accounting': 'accounting',
}

export const getDomainMetaBySlug = (slug: string): ExceptionDomainMeta =>
  getDomainMeta(SLUG_TO_DOMAIN[slug] ?? slug)